import type { SchemaInput } from '../shared/schema-input.js';
import { isString } from '../shared/runtime-values.js';
import type { RouteMode, RoutePlanResult, RouteOption, RoutePlanFailureReason } from './route-planner-routing.js';
import {
  createBlankRoutePlannerWaypoint,
  isResolvedRoutePlannerWaypoint,
  normalizeRoutePlannerWaypoints,
  normalizeRoutePlannerWaypoint,
  routePlannerWaypointLabel,
  type RoutePlannerWaypointEntry,
} from './route-planner-waypoints.js';
import type { MeasurementScene } from './distance-renderer.js';

export type RoutePlannerMode = RouteMode;
export type RoutePlannerStatus = 'idle' | 'loading' | 'error' | 'ready';
type RoutePlannerCalculation =
  | { status: 'idle' }
  | { status: 'loading'; result: RoutePlanResult | null }
  | { status: 'ready'; result: RoutePlanResult }
  | { status: 'error'; result: RoutePlanResult | null; reason: RoutePlanFailureReason; temporary: boolean };

export interface RoutePlannerStageBoundary {
  afterIndex: number;
  name: string | null;
}

export interface RoutePlannerStageSection {
  index: number;
  boundaryAfter: number | null;
  rowStart: number;
  rowEnd: number;
  routeStart: number;
  routeEnd: number;
  name: string | null;
  collapsed: boolean;
}

export class RoutePlannerModel {
  private waypointEntries: RoutePlannerWaypointEntry[] = [
    createBlankRoutePlannerWaypoint('A'),
    createBlankRoutePlannerWaypoint('B'),
  ];
  mode: RoutePlannerMode = 'driving';
  private calculation: RoutePlannerCalculation = { status: 'idle' };
  get result(): RoutePlanResult | null { return this.calculation.status === 'idle' ? null : this.calculation.result; }
  get status(): RoutePlannerStatus { return this.calculation.status; }
  get routeFailureReason(): RoutePlanFailureReason | null { return this.calculation.status === 'error' ? this.calculation.reason : null; }
  get routeFailureTemporary(): boolean { return this.calculation.status === 'error' && this.calculation.temporary; }
  useMetric = true;
  selectedAlt = 0;
  focusedIndex: number | null = null;
  lookupIndex: number | null = null;
  currentBasemap = '';
  stageBoundaries: RoutePlannerStageBoundary[] = [];
  firstStageName: string | null = null;
  organizingStages = false;
  private collapsedStageIndexes = new Set<number>();

  get waypoints(): readonly RoutePlannerWaypointEntry[] { return this.waypointEntries; }

  startRoute(retainResult = false): void {
    this.calculation = { status: 'loading', result: retainResult ? this.result : null };
  }

  setRoute(result: RoutePlanResult): void {
    this.calculation = { status: 'ready', result };
    if (this.selectedAlt > result.alternatives.length) this.selectedAlt = 0;
  }

  failRoute(reason: RoutePlanFailureReason, temporary = false): void {
    this.calculation = { status: 'error', result: this.result, reason, temporary };
  }

  resetRoute(): void { this.calculation = { status: 'idle' }; }

  set waypoints(values: readonly SchemaInput[]) {
    this.waypointEntries = normalizeRoutePlannerWaypoints(values);
  }

  setWaypoint(index: number, value: SchemaInput): RoutePlannerWaypointEntry | undefined {
    if (!Number.isInteger(index) || index < 0 || index >= this.waypointEntries.length) return;
    this.waypointEntries[index] = normalizeRoutePlannerWaypoint(value, routePlannerWaypointLabel(index));
    this.relabelWaypoints();
    return this.waypointEntries[index];
  }

  insertWaypoint(index = this.waypointEntries.length): void {
    if (!Number.isInteger(index) || index < 0 || index > this.waypointEntries.length) return;
    this.waypointEntries.splice(index, 0, createBlankRoutePlannerWaypoint());
    this.relabelWaypoints();
  }

  removeWaypoint(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= this.waypointEntries.length || this.waypointEntries.length <= 2) return;
    this.waypointEntries.splice(index, 1);
    this.relabelWaypoints();
  }

  moveWaypoint(index: number, target: number): void {
    if (![index, target].every(value => Number.isInteger(value) && value >= 0 && value < this.waypointEntries.length)) return;
    const [stop] = this.waypointEntries.splice(index, 1);
    this.waypointEntries.splice(target, 0, stop);
    this.relabelWaypoints();
  }

  private relabelWaypoints(): void {
    this.waypointEntries = this.waypointEntries.map((waypoint, index) => waypoint.label === routePlannerWaypointLabel(index)
      ? waypoint : { ...waypoint, label: routePlannerWaypointLabel(index) });
  }

  resolvedWaypoints() {
    return this.waypoints.filter(isResolvedRoutePlannerWaypoint);
  }

  selectedRoute(): RouteOption | null {
    if (!this.result) return null;
    return [this.result.primary, ...this.result.alternatives][this.selectedAlt] ?? this.result.primary;
  }

  setStages(boundaries: ReadonlyArray<RoutePlannerStageBoundary>, firstStageName: string | null = null): void {
    this.stageBoundaries = boundaries.map((boundary) => ({ ...boundary }));
    this.firstStageName = normalizeStageName(firstStageName);
    this.normalizeStages();
    this.organizingStages = this.stageBoundaries.length > 0;
    this.collapsedStageIndexes.clear();
  }

  createStageAfter(afterIndex: number): boolean {
    this.normalizeStages();
    if (!Number.isInteger(afterIndex) || afterIndex < 0 || afterIndex >= this.waypoints.length - 1) return false;
    if (this.stageBoundaries.some((boundary) => boundary.afterIndex === afterIndex)) return false;
    this.stageBoundaries.push({ afterIndex, name: null });
    this.normalizeStages();
    this.collapsedStageIndexes.clear();
    return true;
  }

  removeStageAfter(afterIndex: number): boolean {
    const index = this.stageBoundaries.findIndex((boundary) => boundary.afterIndex === afterIndex);
    if (index === -1) return false;
    this.stageBoundaries.splice(index, 1);
    if (!this.stageBoundaries.length) this.firstStageName = null;
    this.collapsedStageIndexes.clear();
    return true;
  }

  renameStage(stageIndex: number, name: string): boolean {
    this.normalizeStages();
    if (!this.stageBoundaries.length || !Number.isInteger(stageIndex) || stageIndex < 0 || stageIndex > this.stageBoundaries.length) return false;
    const normalized = normalizeStageName(name);
    if (stageIndex === 0) {
      if (this.firstStageName === normalized) return false;
      this.firstStageName = normalized;
      return true;
    }
    const boundary = this.stageBoundaries[stageIndex - 1];
    if (!boundary || boundary.name === normalized) return false;
    boundary.name = normalized;
    return true;
  }

  toggleStageCollapsed(stageIndex: number): boolean {
    const sections = this.stageSections();
    if (!sections[stageIndex]) return false;
    if (this.collapsedStageIndexes.has(stageIndex)) {
      this.collapsedStageIndexes.delete(stageIndex);
    } else {
      this.collapsedStageIndexes.add(stageIndex);
    }
    return true;
  }

  stageSections(): RoutePlannerStageSection[] {
    this.normalizeStages();
    const lastWaypointIndex = this.waypoints.length - 1;
    const sections: RoutePlannerStageSection[] = [];
    let rowStart = 0;
    let routeStart = 0;

    for (let index = 0; index <= this.stageBoundaries.length; index += 1) {
      const boundary = this.stageBoundaries[index];
      const routeEnd = boundary?.afterIndex ?? lastWaypointIndex;
      const name = index === 0 ? this.firstStageName : this.stageBoundaries[index - 1]?.name ?? null;
      sections.push({
        index,
        boundaryAfter: index === 0 ? null : this.stageBoundaries[index - 1]?.afterIndex ?? null,
        rowStart,
        rowEnd: routeEnd,
        routeStart,
        routeEnd,
        name,
        collapsed: this.collapsedStageIndexes.has(index),
      });
      rowStart = routeEnd + 1;
      routeStart = routeEnd;
    }
    return sections;
  }

  sharedStageBoundaries(): RoutePlannerStageBoundary[] {
    this.normalizeStages();
    const resolvedTotal = this.waypoints.filter(isResolvedRoutePlannerWaypoint).length;
    const seen = new Set<number>();
    const shared: RoutePlannerStageBoundary[] = [];
    for (const boundary of this.stageBoundaries) {
      const resolvedBefore = this.waypoints
        .slice(0, boundary.afterIndex + 1)
        .filter(isResolvedRoutePlannerWaypoint)
        .length;
      const afterIndex = resolvedBefore - 1;
      if (afterIndex < 0 || afterIndex >= resolvedTotal - 1 || seen.has(afterIndex)) continue;
      seen.add(afterIndex);
      shared.push({ afterIndex, name: boundary.name });
    }
    return shared;
  }

  onWaypointInserted(index: number): void {
    if (!Number.isInteger(index) || index < 0) return;
    for (const boundary of this.stageBoundaries) {
      if (boundary.afterIndex >= index) boundary.afterIndex += 1;
    }
    this.normalizeStages();
    this.collapsedStageIndexes.clear();
  }

  onWaypointRemoved(index: number): void {
    if (!Number.isInteger(index) || index < 0) return;
    for (const boundary of this.stageBoundaries) {
      if (boundary.afterIndex >= index) boundary.afterIndex -= 1;
    }
    this.normalizeStages();
    this.collapsedStageIndexes.clear();
  }

  private normalizeStages(): void {
    const maximumBoundary = this.waypoints.length - 2;
    const seen = new Set<number>();
    this.stageBoundaries = this.stageBoundaries
      .filter((boundary) => Number.isInteger(boundary.afterIndex) && boundary.afterIndex >= 0 && boundary.afterIndex <= maximumBoundary)
      .sort((a, b) => a.afterIndex - b.afterIndex)
      .filter((boundary) => {
        if (seen.has(boundary.afterIndex)) return false;
        seen.add(boundary.afterIndex);
        boundary.name = normalizeStageName(boundary.name);
        return true;
      });
    if (!this.stageBoundaries.length) this.firstStageName = null;
    this.firstStageName = normalizeStageName(this.firstStageName);
  }

  measurementScene(): MeasurementScene {
    const route = this.selectedRoute();
    return {
      waypoints: this.resolvedWaypoints().map(({ lat, lng, label, address }) => ({ lat, lng, label, address })),
      polygonClosed: false,
      useMetric: this.useMetric,
      route: route ? {
        distanceKm: route.distanceKm,
        durationSec: route.durationSec,
        legDistancesKm: route.legDistancesKm,
        legCoords: [route.coords],
        legGapCoords: [null],
        allLegsRouted: true,
      } : null,
      routeMode: this.mode,
    };
  }
}

function normalizeStageName(value: SchemaInput): string | null {
  if (!isString(value)) return null;
  const normalized = value.trim().slice(0, 80);
  return normalized || null;
}

export function createRoutePlannerState(): RoutePlannerModel {
  return new RoutePlannerModel();
}
